import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  parseISO,
} from "date-fns"
import {
  Check,
  Copy,
  Facebook,
  Mail,
  Send,
  Linkedin,
  Instagram,
  Youtube,
  Github,
} from "lucide-svelte"
import { t } from "$lib/utils/i18n"
import { PUBLIC_HASH, PUBLIC_HMAC_SECRET_KEY } from "$env/static/public"
import { encryptJson, generateHmac } from "$lib/utils/encryption"

export function pluralize(
  count: number,
  single: string,
  plural: string,
): string {
  return count === 1 ? single : plural
}

export const getRequirement = (action: any, name: string) =>
  action.selectedAction?.requirements
    ? action.selectedAction.requirements.find((r: any) => r.name === name)
        ?.value
    : action.requirements.find((r: any) => r.name === name)?.value

export function isCompetitionEnded(endDate: string): boolean {
  return new Date() > new Date(endDate)
}

interface ParentUrlMessageEvent extends MessageEvent {
  data: {
    type: string
    url?: string
  }
}

export async function getParentUrl(): Promise<string> {
  return new Promise((resolve) => {
    const messageHandler = (event: ParentUrlMessageEvent) => {
      if (event.data.type === "PARENT_URL_RESPONSE") {
        window.removeEventListener("message", messageHandler)
        resolve(event.data.url || "")
      }
    }
    window.addEventListener("message", messageHandler)
    window.parent.postMessage({ type: "REQUEST_PARENT_URL" }, "*")
  })
}

export async function updateCompetitionEntry(
  user,
  competition,
  action,
  verificationData = null,
  fingerprint,
  encryption,
) {
  if (!user?.email || !competition.id) return

  const { encrypted: requestId } = await encryptJson(
    {
      userName: user.name,
      userEmail: user.email,
      competitionId: competition.id,
      timestamp: new Date().toISOString(),
    },
    PUBLIC_HASH,
  )

  try {
    const requestBody = JSON.stringify({
      action,
      teamId: competition.teamId,
      verificationData,
      userEmail: user.email,
      competitionId: competition.id,
      userName: user.name,
      fingerprint,
      encryption,
      requestId,
    })

    // Generate HMAC signature if public key is available
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    }

    if (PUBLIC_HMAC_SECRET_KEY) {
      const hmacSignature = await generateHmac(
        requestBody,
        PUBLIC_HMAC_SECRET_KEY,
      )
      headers["x-hmac-signature"] = hmacSignature
    }

    const response = await fetch("/api/competitions/create-or-update-entry", {
      method: "POST",
      headers,
      body: requestBody,
    })

    if (!response.ok) {
      const error = await response.json()
      console.error("Error updating entry:", error)
      return
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error("Error updating entry:", error)
  }
}

const findHandleValue = (name: string, ...objects: any[]) => {
  for (const obj of objects) {
    const value = obj?.requirements?.find((r) => r.name === name)?.value
    if (value) return value
  }

  return "....."
}

function extractFacebookHandle(url: string): string {
  try {
    const match = url.match(/facebook\.com\/people\/([^\/]+)/)
    return match
      ? match[1]
      : url
          .replace("https://facebook.com/", "")
          .replace("https://www.facebook.com/", "")
  } catch (error) {
    console.error("Error extracting Facebook handle:", error)
    return ""
  }
}

export function getActionName(action: any, language: string = "en"): string {
  const platform = action.platform || action.actions
  const selectedAction = action.selectedAction || action

  if (platform) {
    if (
      (platform === "Instagram" || platform.platform === "Instagram") &&
      (selectedAction.name === "Visit Profile on Instagram" ||
        platform.name === "Visit Profile on Instagram")
    ) {
      const handle = findHandleValue("Username", selectedAction, action)
      return t("widget_actions_visit_profile_on_instagram", language, {
        handle: `<u class="font-[500]">@${handle}</u>`,
      })
    } else if (
      (platform === "Instagram" || platform.platform === "Instagram") &&
      (selectedAction.name === "Follow on Instagram" ||
        platform.name === "Follow on Instagram")
    ) {
      const handle = findHandleValue("Username", selectedAction, action)
      return t("widget_actions_follow_on_instagram", language, {
        handle: `<u class="font-[500]">@${handle}</u>`,
      })
    } else if (
      (platform === "TikTok" || platform.platform === "TikTok") &&
      (selectedAction.name === "Follow on TikTok" ||
        platform.name === "Follow on TikTok")
    ) {
      const handle = findHandleValue("Username", selectedAction, action)
      return t("widget_actions_follow_on_tiktok", language, {
        handle: `<u class="font-[500]">@${handle}</u>`,
      })
    } else if (
      (platform === "Facebook" || platform.platform === "Facebook") &&
      (selectedAction.name === "Visit a Facebook Page" ||
        platform.name === "Visit a Facebook Page")
    ) {
      const handle = findHandleValue("URL", selectedAction, action)
        ?.replace("https://www.facebook.com/", "")
        .replace("https://facebook.com/", "")

      return t("widget_actions_visit_facebook_page", language, {
        handle: `<u class="font-[500]">@${handle}</u>`,
      })
    } else if (
      (platform === "Facebook" || platform.platform === "Facebook") &&
      (selectedAction.name === "Like on Facebook" ||
        platform.name === "Like on Facebook")
    ) {
      let handle = findHandleValue("Username", selectedAction, action)?.replace(
        "https://www.facebook.com/",
        "",
      )

      if (handle === "....." || handle.includes("people/")) {
        handle = extractFacebookHandle(
          findHandleValue("URL", selectedAction, action),
        )
      }

      if (handle === "....." || handle.includes("people/")) {
        handle = extractFacebookHandle(
          findHandleValue("Username", selectedAction, action),
        )
      }

      return t("widget_actions_like_on_facebook", language, {
        handle: `<u class="font-[500]">@${handle}</u>`,
      })
    } else if (
      (platform === "X (Twitter)" || platform.platform === "X (Twitter)") &&
      (selectedAction.name === "Follow on X" || platform.name === "Follow on X")
    ) {
      const handle = findHandleValue("Username", selectedAction, action)
      return t("widget_actions_follow_on_x", language, {
        handle: `<u class="font-[500]">@${handle}</u>`,
      })
    } else if (
      (platform === "LinkedIn" || platform.platform === "LinkedIn") &&
      (selectedAction.name === "Follow Company on LinkedIn" ||
        platform.name === "Follow Company on LinkedIn")
    ) {
      const handle = findHandleValue("Company Name", selectedAction, action)
      return t("widget_actions_follow_company_on_linkedin", language, {
        handle,
      })
    }

    const title = findHandleValue("Title", selectedAction, action)
    if (title !== ".....") {
      return title
    }

    if (language === "en") {
      return `${selectedAction?.actions?.name || selectedAction?.name}`
    } else {
      return `${selectedAction?.actions?.translations[language]?.name}`
    }
  }

  return t("widget_action_unknown_action", language)
}

export function getTimeLeft(endDate: string | undefined): string {
  if (!endDate) {
    return "N/A"
  }

  try {
    const now = new Date()
    const end = parseISO(endDate)

    if (isNaN(end.getTime())) {
      return `${t("widget_time_invalid_date")}`
    }

    if (end < now) {
      return `${t("widget_time_ended")}`
    }

    const daysLeft = differenceInDays(end, now)

    if (daysLeft > 1) {
      return `${daysLeft} ${t("widget_time_days")}`
    } else if (daysLeft === 1) {
      return `1 ${t("widget_time_day")}`
    } else {
      const hoursLeft = differenceInHours(end, now)
      if (hoursLeft > 1) {
        return `${hoursLeft} ${t("widget_time_hours")}`
      } else if (hoursLeft === 1) {
        return `1 ${t("widget_time_hour")}`
      } else {
        const minutesLeft = differenceInMinutes(end, now)
        return `${minutesLeft} ${t("widget_time_minutes")}`
      }
    }
  } catch (error) {
    console.error("Error parsing date:", error)
    return "Error parsing date"
  }
}

export const platformIcons = {
  email: Mail,
  facebook: Facebook,
  twitter: {
    svg: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4l11.733 16h4.267l-11.733 -16z"/><path d="M4 20l6.768 -6.768m2.46 -2.46l6.772 -6.772"/></svg>`,
  },
  tumblr: {
    svg: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 21h4v-4h-4v-6h4V7h-4V3h-4v1a3 3 0 0 1-3 3H6v4h4v6a4 4 0 0 0 4 4"/></svg>`,
  },
  telegram: Send,
  linkedin: Linkedin,
  instagram: Instagram,
  youtube: Youtube,
  github: Github,
  pinterest: {
    svg: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/></svg>`,
  },
  whatsapp: {
    svg: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>`,
  },
}

export const platformColors = {
  email: "bg-gray-800",
  facebook: "bg-[#1877F2]",
  twitter: "bg-black",
  tumblr: "bg-[#34526f]",
  telegram: "bg-[#0088cc]",
  linkedin: "bg-[#0077b5]",
  instagram: "bg-[#e4405f]",
  youtube: "bg-[#ff0000]",
  github: "bg-[#333]",
  pinterest: "bg-[#bd081c]",
  whatsapp: "bg-[#25D366]",
}

export function shareOnPlatform(
  platform: string,
  referralLink: string,
  title: string,
) {
  const shareText = `I've just entered for the chance to ${title}. Enter for your chance to win. ${referralLink}`
  const shareUrl = encodeURIComponent(referralLink)

  let shareLink
  switch (platform) {
    case "email":
      if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        // On mobile, try to detect and use native sharing if available
        if (navigator.share) {
          navigator
            .share({
              title: title,
              text: shareText,
              url: referralLink,
            })
            .catch((err) => {
              // Fallback to mailto if share API fails
              shareLink = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(shareText)}`
            })
          return
        }
      }
      // Desktop or fallback
      shareLink = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(shareText)}`
      break
    case "facebook":
      shareLink = `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`
      break
    case "twitter":
      shareLink = `https://twitter.com/intent/tweet?text=${shareText}`
      break
    case "tumblr":
      shareLink = `https://www.tumblr.com/share/link?url=${shareUrl}&name=${encodeURIComponent(title)}&description=${shareText}`
      break
    case "telegram":
      shareLink = `https://t.me/share/url?url=${shareUrl}&text=${shareText}`
      break
    case "linkedin":
      shareLink = `https://www.linkedin.com/shareArticle?mini=true&url=${shareUrl}&title=${encodeURIComponent(title)}&summary=${shareText}`
      break
    case "instagram":
      shareLink = `instagram://`
      break
    case "youtube":
      shareLink = `https://www.youtube.com/`
      break
    case "github":
      shareLink = `https://github.com/`
      break
    case "pinterest":
      shareLink = `https://pinterest.com/pin/create/button/?url=${shareUrl}&description=${shareText}`
      break
    case "whatsapp":
      shareLink = `https://api.whatsapp.com/send?text=${shareText}`
      break
    default:
      console.warn(`Sharing for platform "${platform}" is not implemented.`)
      return
  }

  if (shareLink) {
    if (platform === "email") {
      window.location.href = shareLink
    } else {
      window.open(shareLink, "_blank", "noopener,noreferrer")
    }
  }
}
